import { faEye, faEyeSlash, faLinkSimple } from '@fortawesome/pro-light-svg-icons';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../shared/components/alert/Alert';
import { Button } from '../../../shared/components/button/Button';
import { Link } from '../../../shared/components/link/Link';
import ChevronBackBtn from '../../../shared/components/styledComponents/chevronBackBtn/ChevronBackBtn';
import { AuthContext } from '../../../shared/context/auth-context';
import { GreenCheckOverlay } from '../../../shared/modal/greenCheckOverlay/GreenCheckOverlay';
import { PasscodeState } from './types';
import { usePasscodeChange } from './usePasscodeChange';
import './SecurityDetails.css';

type ChangePasscodeRightMenuProps = {
  hide: boolean;
  profile: any;
  changeMenu: (_menuId: number, _action: string) => void;
  activeSubMenuId?: number;
};

export function ChangePasscodeRightMenu({
  hide,
  profile,
  changeMenu,
  activeSubMenuId = 53,
}: ChangePasscodeRightMenuProps) {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);

  const {
    isAfterSubmit,
    passcodeFields,
    passcodeState,
    showGreenCheck,
    errorSummary,
    getFieldError,
    handleChangePasscode,
    handlePasscodeChange,
    handleFieldBlur,
    announcement,
  } = usePasscodeChange();

  const renderPasscodeField = useCallback(
    (
      fieldName: keyof PasscodeState,
      fieldDescription: string,
      showPasscode: boolean,
      toggleShowPasscode: () => void,
      rulesDescription?: string
    ) => {
      const { hasError, message } = getFieldError(fieldName);
      const formattedContent = rulesDescription?.replace(/\n/g, '<br>');

      return (
        <fieldset
          key={fieldName}
          className={`password_container${hasError ? ' error' : ''}`}
          aria-labelledby={`edit-title ${fieldName}-legend`}
          aria-describedby={formattedContent ? `passcode_field_rules_${fieldName}` : undefined}
        >
          <legend className="not_logged_label password_field_legend" id={`${fieldName}-legend`}>
            {hasError && isAfterSubmit && t('error')}
            {fieldDescription}
            {t('input.label.required')}
          </legend>

          {formattedContent && (
            <span
              id={`passcode_field_rules_${fieldName}`}
              className="password_field_rules"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: formattedContent }}
            />
          )}
          <div className="password_field_input_wrapper">
            <input
              className="code_input password_field_input"
              type={showPasscode ? 'text' : 'password'}
              name={fieldName}
              id={fieldName}
              value={passcodeState[fieldName] as string}
              onChange={handlePasscodeChange}
              onBlur={handleFieldBlur}
              aria-labelledby={`${fieldName}-legend`}
            />
            <Button
              ariaPressed={showPasscode}
              ariaLabel={
                showPasscode
                  ? t('passcode_visible.click_to_hide')
                  : t('passcode_hidden.click_to_show')
              }
              onClick={toggleShowPasscode}
              icon={{
                faIconDefinition: showPasscode ? faEye : faEyeSlash,
                color: 'white',
                size: 'sm',
                className: 'icon',
              }}
              variant="ghost"
              color="pink"
              className="password_field_preview_button"
            />
          </div>
          <Alert
            id={`${fieldName}-error-alert`}
            isVisible={hasError}
            message={message}
            type="error"
            variant="outline"
            size="md"
          />
        </fieldset>
      );
    },
    [passcodeState, handlePasscodeChange, handleFieldBlur, t, getFieldError, isAfterSubmit]
  );

  return (
    profile && (
      <>
        {showGreenCheck && <GreenCheckOverlay text={t('saved_changes_successfully')} />}
        <fieldset
          role="tabpanel"
          id="panel-change-passcode"
          aria-labelledby="tab-change-passcode-title"
          tabIndex={activeSubMenuId === 53 ? 0 : -1}
          hidden={activeSubMenuId !== 53}
          className="edit-field fs-exclude password_container"
        >
          {hide && (
            <div className="btn-cont ">
              <ChevronBackBtn onClick={() => changeMenu(0, 'back')} />
            </div>
          )}
          <h2 className="edit-title" id="tab-change-passcode-title">
            {t('S028')}
          </h2>
          {errorSummary.length > 0 && isAfterSubmit && (
            <Alert
              id="error_summary_change_passcode"
              isAssessive
              isVisible
              message={
                <ol>
                  {errorSummary
                    .sort((a, b) => {
                      const order = ['currentPasscode', 'newPasscode', 'confirmNewPasscode'];
                      return order.indexOf(a) - order.indexOf(b);
                    })
                    .map((error) => (
                      <li key={error}>
                        <a href={`#${error}`}>
                          {t(
                            passcodeFields.find((field) => field.name === error)?.descriptionKey
                          ) || t('error')}
                        </a>
                      </li>
                    ))}
                </ol>
              }
              title={{
                text:
                  errorSummary.length > 1
                    ? t('error.summary.title.fields.many', { errorCount: errorSummary.length })
                    : t('error.summary.title.fields.one'),
                showTitle: true,
              }}
              type="error"
            />
          )}
          <form onSubmit={handleChangePasscode}>
            {passcodeFields.map((field) =>
              renderPasscodeField(
                field.name,
                t(field.descriptionKey),
                field.showPasscode,
                field.toggleFunction,
                field.rulesDescription
              )
            )}
            <Link
              href="/reset"
              color="blue"
              icon={{ faIconDefinition: faLinkSimple }}
              onClick={() => {
                auth.logout({ shouldRedirect: false });
              }}
              isWrapText
            >
              {t('settings.security.changePassword.link')}
            </Link>
            <Button type="submit" className="wide margin-top-24">
              {t('S028')}
            </Button>
          </form>
        </fieldset>
        <div
          aria-live="assertive"
          aria-atomic="true"
          role="alert"
          className="visuallyHidden"
          id="passcode-validation-announcement"
        >
          {announcement}
        </div>
      </>
    )
  );
}
